<script setup>
    import { useFormularioPago } from '@/composables/front/common/useFormularioPago.js';

    const props = defineProps({
        jet_id: {
            type: String,
            required: true
        },
        session_id: {
            type: String,
            default: ''
        },
        tipo_venta: {
            type: String,
            default: 'evento'
        },
        esPlazos: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['payToken', 'cancel']);

    const {
        anosCaducidad,
        getPayToken,
        cancelarCompra
    } = useFormularioPago(props, emit);

    const onPayToken = jetToken => emit('payToken', jetToken);
    const onCancel = () => emit('cancel');
</script>

<template>
    <!--
      Shell-less on purpose: the outer card border, the "Pago con tarjeta" header
      and the trust strip live in PaymentStep.vue so the whole payment step reads
      as a single card (matching the Paycomet hosted-checkout reference). This
      component renders only the card-data zone: holder on top, then one grouped
      input row holding PAN + expiry + CVC behind a single border.
    -->
    <form
        data-test="card-payment-form"
        class="tw:space-y-3"
        role="form"
        id="paycometPaymentForm"
        action="javascript:void(0);"
        @submit.prevent="getPayToken"
        method="POST"
    >
        <input id="jetID" type="hidden" data-paycomet="jetID" :value="jet_id" />

        <!-- Holder (separate input on top) -->
        <div>
            <label
                for="inputNombre"
                class="tw:block tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.18em] tw:text-site/55 tw:mb-1.5"
            >
                {{ $t('formulario_pago.titular') }}
            </label>
            <input
                type="text"
                id="inputNombre"
                class="tw:w-full tw:px-4 tw:py-3 tw:rounded-xl tw:border-2 tw:border-site/15 tw:bg-white tw:text-site tw:text-sm tw:outline-none tw:transition-colors focus:tw:border-alt placeholder:tw:text-site/35"
                name="username"
                data-paycomet="cardHolderName"
                :placeholder="$t('formulario_pago.titular')"
                autocomplete="cc-name"
                required
            />
        </div>

        <!-- Grouped PAN + expiry + CVC behind a single border.
             Mobile (<sm): PAN on its own row (full width), then expiry + CVC
             sharing the next row. From sm up: PAN on the left (flex-1),
             expiry + CVC on the right (compact fixed-width group).
             Separators use gap-px over a tinted background so they don't
             double up with the container's border. -->
        <div>
            <span class="tw:sr-only">{{ $t('formulario_pago.n_tarjeta') }}</span>
            <div
                class="card-row tw:flex tw:flex-col tw:sm:flex-row tw:items-stretch tw:gap-px tw:bg-site/12 tw:border-2 tw:border-site/15 tw:rounded-xl tw:overflow-hidden tw:transition-colors focus-within:tw:border-alt"
            >
                <!-- PAN cell with brand mark -->
                <div class="tw:flex tw:items-center tw:gap-2 tw:px-3 tw:bg-white tw:flex-1 tw:min-w-0">
                    <svg
                        viewBox="0 0 36 24"
                        class="tw:h-5 tw:w-8 tw:flex-shrink-0"
                        aria-hidden="true"
                    >
                        <circle cx="14" cy="12" r="9" fill="#EB001B" />
                        <circle cx="22" cy="12" r="9" fill="#F79E1B" />
                        <path d="M18 5.5a9 9 0 0 0 0 13 9 9 0 0 0 0-13z" fill="#FF5F00" />
                    </svg>
                    <div class="tw:flex-1 tw:min-w-0">
                        <div id="paycomet-pan" style="padding:0px; height:36px;"></div>
                        <input
                            id="inputNTarjeta"
                            paycomet-style="width: 100%; height: 21px; font-size:14px; padding: 7px 0px 7px 0px; outline:none !important; border:0; background:transparent;"
                            paycomet-name="pan"
                            paycomet-placeholder="5234 5234 5234 5234"
                            aria-label="Número de tarjeta"
                        />
                    </div>
                </div>

                <!-- Expiry + CVC grouped together so they share a row on
                     mobile and sit side-by-side with the PAN on sm+. -->
                <div class="tw:flex tw:flex-row tw:items-stretch tw:gap-px tw:bg-site/12 tw:sm:flex-none">
                    <!-- Expiry cell -->
                    <div class="tw:flex tw:items-center tw:gap-1 tw:px-3 tw:bg-white tw:flex-1 tw:sm:flex-none">
                        <select
                            id="mesCaducidad"
                            class="tw:border-0 tw:bg-transparent tw:text-site tw:text-sm tw:font-medium tw:outline-none tw:cursor-pointer tw:px-0.5"
                            data-paycomet="dateMonth"
                            aria-label="Mes de caducidad"
                        >
                            <option selected disabled>MM</option>
                            <option value="01">01</option>
                            <option value="02">02</option>
                            <option value="03">03</option>
                            <option value="04">04</option>
                            <option value="05">05</option>
                            <option value="06">06</option>
                            <option value="07">07</option>
                            <option value="08">08</option>
                            <option value="09">09</option>
                            <option value="10">10</option>
                            <option value="11">11</option>
                            <option value="12">12</option>
                        </select>
                        <span class="tw:text-site/35 tw:font-bold" aria-hidden="true">/</span>
                        <select
                            id="anoCaducidad"
                            class="tw:border-0 tw:bg-transparent tw:text-site tw:text-sm tw:font-medium tw:outline-none tw:cursor-pointer tw:px-0.5"
                            data-paycomet="dateYear"
                            aria-label="Año de caducidad"
                        >
                            <option selected disabled>AA</option>
                            <template v-for="anoCaducidad in anosCaducidad">
                                <option :key="anoCaducidad" :value="anoCaducidad.toString().slice(-2)">{{ anoCaducidad }}</option>
                            </template>
                        </select>
                    </div>

                    <!-- CVC cell. Visible inline label so users know what
                         to type without relying on the iframe placeholder.
                         flex-1 on mobile, fixed compact width on sm+. -->
                    <div class="tw:flex tw:items-center tw:gap-1 tw:px-3 tw:bg-white tw:flex-1 tw:sm:flex-none tw:sm:w-28 tw:min-w-0">
                        <span class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-wider tw:text-site/55 tw:shrink-0">CVC</span>
                        <div id="paycomet-cvc2" style="height: 36px; padding:0px; width: 100%;"></div>
                        <input
                            paycomet-name="cvc2"
                            paycomet-style="border:0px; width: 100%; height: 21px; padding-top: 7px; font-size:13px; padding-left:0px; outline:none !important; background:transparent;"
                            paycomet-placeholder="CVC"
                            aria-label="CVC"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="error-paycomet" id="paymentErrorMsg"></div>

        <div class="tw:pt-1 tw:flex tw:flex-row tw:items-center tw:gap-3">
            <button
                type="submit"
                id="confirmar-tarjeta"
                data-cy="confirmar-tarjeta"
                data-test="card-confirm"
                class="tw:flex-1 tw:py-3 tw:bg-site tw:text-white tw:font-bold tw:uppercase tw:tracking-[0.15em] tw:rounded-md tw:text-sm hover:tw:bg-black tw:transition-colors focus:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-site focus-visible:tw:ring-offset-2 tw:flex tw:items-center tw:justify-center tw:gap-2"
            >
                <span>{{ $t('formulario_pago.confirmar') }}</span>
                <svg
                    class="tw:w-4 tw:h-4 tw:opacity-80"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
            </button>
            <button
                type="button"
                id="cancelar-tarjeta"
                data-cy="cancelar-tarjeta"
                data-test="card-cancel"
                class="tw:shrink-0 tw:px-4 tw:py-3 tw:border-2 tw:border-site/20 tw:text-site tw:text-sm tw:font-semibold tw:uppercase tw:tracking-wider tw:rounded-md hover:tw:border-site/40 hover:tw:bg-site/[0.025] tw:transition-colors focus:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-site focus-visible:tw:ring-offset-2"
                @click="onCancel"
            >
                {{ $t('formulario_pago.cancelar') }}
            </button>
        </div>
    </form>
</template>

<style scoped>
.error-paycomet {
    color: #D8000C;
    background-color: #FFBABA;
    padding: 3px;
    border-radius: 6px;
    margin-top: 7px;
    padding-left: 15px;
}

.error-paycomet:empty {
    display: none;
}
</style>
